import React, { useEffect, useRef, useState, useLayoutEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchSignatureRequestByToken,
  fillSignatureBox,
  submitSignatureRequest,
} from "../../../../services/fileServices/signatures/SignatureService";
import { SignatureRequest } from "../../../../interfaces/file/fileSignature/SignatureRequest";
import {
  SignatureBox,
  SignatureField,
  SignatureType,
} from "../../../../interfaces/file/fileSignature/SignatureBox";
import { getFileByIdByToken } from "../../../../services/fileServices/FileService";
import { getInitials } from "../../../../utilities/stringUtils";
import { getTodayDateString } from "../../../../utilities/dateUtils";
import PDFViewer from "./PDFViewer";
import GuestSignHeader from "./guestSignComponents/GuestSignHeader";
import GuestSidebar from "./guestSignComponents/GuestSidebar";
import Loader from "../../loader/Loader";
import EnterNameStep from "./guestSignComponents/EnterNameStep";
import DisclosureAgreement from "./guestSignComponents/DisclosureAgreement";
import SuccessView from "./guestSignComponents/SuccessView";
import { Button } from "react-bootstrap";
import { retryWithExponentialBackoff } from "../../../../utilities/apiUtils";
import GuestPDFViewerControls from "./guestSignComponents/GuestPDFViewerControls";
import GuestLayout from "../../../layouts/GuestLayout";

type SigningStep = "loading" | "disclosure" | "viewing" | "adoptingSignature" | "signing" | "success" | "error";

const GuestSignPage: React.FC = () => {
  const { token } = useParams();

  const [step, setStep] = useState<SigningStep>("loading");
  const [userName, setUserName] = useState("");
  const [userInitials, setUserInitials] = useState("");

  const [requestData, setRequestData] = useState<SignatureRequest | null>(null);
  const [fileBlobUrl, setFileBlobUrl] = useState<string>("");
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [errorMessage, setErrorMessage] = useState("");
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
  const [showTextModal, setShowTextModal] = useState(false);
  const [textModalValue, setTextModalValue] = useState("");
  const [activeSignatureBox, setActiveSignatureBox] = useState<SignatureBox | null>(null);
  const [activeNav, setActiveNav] = useState<string | null>("signature-boxes");
  const [containerWidth, setContainerWidth] = useState(0);
  const [pendingSignatureBox, setPendingSignatureBox] = useState<SignatureBox | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  const fetchData = useCallback(async (isInitialLoad = false) => {
    if (!token) {
      setErrorMessage("No token provided");
      setStep("error");
      return;
    }

    try {
      const [data, fileUrl] = await retryWithExponentialBackoff(() =>
        Promise.all([
          fetchSignatureRequestByToken(token),
          getFileByIdByToken(token)
        ])
      );
      if (!data || !fileUrl) {
        setErrorMessage("Invalid or expired signature request");
        setStep("error");
        return;
      }
      setRequestData(data);
      if (isInitialLoad) {
        setFileBlobUrl(fileUrl);
      }

      if (isInitialLoad) {
        setStep("disclosure");
      }

    } catch (err) {
      setErrorMessage("Failed to load signature request after multiple attempts.");
      setStep("error");
    }
  }, [token]);

  useLayoutEffect(() => {
    if (pdfContainerRef.current) {
      const w = Math.floor(pdfContainerRef.current.getBoundingClientRect().width);
      if (w) setContainerWidth(w);
    }
  }, [step]);

  useEffect(() => {
    if (!pdfContainerRef.current) return;
    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        const w = Math.floor(entry.contentRect.width);
        if (w !== containerWidth) setContainerWidth(w);
      }
    });
    ro.observe(pdfContainerRef.current);
    return () => ro.disconnect();
  }, [containerWidth]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  const fillBoxValue = async (box: SignatureBox, value: string) => {
    if (!token || !requestData) return false;

    setIsProcessing(true);
    const success = await fillSignatureBox(token, box.id, value);

    if (!success) {
      setErrorMessage("Error signing field.");
      setIsProcessing(false);
      return false;
    }

    // Refetch data to get the updated state after successful signature
    try {
      const updatedData = await fetchSignatureRequestByToken(token);
      if (updatedData) {
        setRequestData(updatedData);
        
        // Check if the current user has actually signed at least one of their assigned boxes
        const userSignedBoxes = updatedData.signatureBoxes.filter(b =>
          b.assignedSignerEmail.toLowerCase() === updatedData.signerEmail.toLowerCase() &&
          b.signatureValue?.trim()
        );

        // Only show success if user has signed all their assigned boxes
        const userAssignedBoxes = updatedData.signatureBoxes.filter(b =>
          b.assignedSignerEmail.toLowerCase() === updatedData.signerEmail.toLowerCase()
        );

        const allUserBoxesSigned = userAssignedBoxes.length > 0 && userSignedBoxes.length === userAssignedBoxes.length;

        if (allUserBoxesSigned) {
          setStep("success");
        }
      }
    } catch (err) {
      console.error("Error refetching signature data:", err);
      // Fallback to local state update if refetch fails
      setRequestData(prev => {
        if (!prev) return null;
        return {
          ...prev,
          signatureBoxes: prev.signatureBoxes.map(b =>
            b.id === box.id ? { ...b, signatureValue: value } : b
          )
        };
      });
    }
    setIsProcessing(false);
    return true;
  };

  const applySignatureToBox = async (box: SignatureBox, adoptedSignature: { name: string, initials: string }) => {
    if (!requestData) return;

    const { name, initials } = adoptedSignature;
    if (!name || !initials) return;

    try {
      let newValue = "";
      switch (box.boxType.name) {
        case SignatureType.SIGNATURE: newValue = name; break;
        case SignatureType.INITIALS: newValue = initials; break;
        case SignatureType.DATE: newValue = getTodayDateString(); break;
        case SignatureType.TEXT:
          setActiveSignatureBox(box);
          setShowTextModal(true);
          return;
        default: return;
      }
      if (await fillBoxValue(box, newValue)) toast.success("Field filled successfully!");
    } catch (err) {
      toast.error("Error filling field.");
    }
  };

  const handleSignBox = async (box: SignatureBox) => {
    if (!requestData) return;
    if (box.assignedSignerEmail.toLowerCase() !== requestData.signerEmail.toLowerCase()) {
      setErrorMessage("You are not authorized to fill this field.");
      return;
    }
    if (box.signatureValue?.trim()) return;

    if (step === "viewing") {
      setPendingSignatureBox(box);
      setStep("adoptingSignature");
      return;
    }

    if (step === "signing") {
      await applySignatureToBox(box, { name: userName, initials: userInitials });
    }
  };

  const handleTextModalSubmit = async () => {
    if (!activeSignatureBox || !textModalValue.trim()) {
      setShowTextModal(false);
      return;
    }
    if (await fillBoxValue(activeSignatureBox, textModalValue.trim())) {
      toast.success("Field filled successfully!");
    }
    setShowTextModal(false);
    setActiveSignatureBox(null);
    setTextModalValue("");
  };

  const handleSelectBox = (boxId: string) => {
    const box = requestData?.signatureBoxes.find(b => b.id === boxId);
    if (box) {
      setCurrentPage(box.pageNumber);
      setSelectedBoxId(boxId);
      scrollContainerRef.current?.scrollTo({ top: box.y - 50, behavior: "smooth" });
    }
  };

  const handleDisclosureAgree = () => {
    setStep("viewing");
  };

  const handleSignatureAdopt = async (signature: { name: string, initials: string }) => {
    setUserName(signature.name);
    setUserInitials(signature.initials);
    setStep("signing");
    if (pendingSignatureBox) {
      await applySignatureToBox(pendingSignatureBox, signature);
      setPendingSignatureBox(null);
    }
  };

  const onSign = (boxId: string) => {
    const box = requestData?.signatureBoxes.find((b) => b.id === boxId);
    if (box) {
      handleSignBox(box);
    }
  };

  const onFinishAndSign = async (boxId: string) => {
    const box = requestData?.signatureBoxes.find((b) => b.id === boxId);
    if (box) {
      await handleSignBox(box);
    }
  };

  if (step === "loading") return <Loader />;
  if (step === "success") return <SuccessView />;
  if (step === "error" || !requestData || !fileBlobUrl) return <div>{errorMessage || "Failed to load document."}</div>;

  const signatureFields: SignatureField[] = requestData.signatureBoxes.map(box => ({
    ...box,
    signerEmail: box.assignedSignerEmail,
    signatureType: box.boxType.name,
  }));

  return (
    <GuestLayout guestUser={requestData.signerEmail}>
      <div className="common_card relative">
        {isProcessing && (
          <div className="absolute inset-0 bg-gray-800 bg-opacity-75 z-50 flex items-center justify-center p-4">
            <Loader />
          </div>
        )}
        {(step === "disclosure" || step === "adoptingSignature") && (
          <div className="absolute inset-0 bg-gray-800 bg-opacity-75 z-40 flex items-center justify-center p-4">
            {step === "disclosure" && (
              <div className="max-w-xl w-full">
                <DisclosureAgreement onAgree={handleDisclosureAgree} />
              </div>
            )}
            {step === "adoptingSignature" && (
              <div className="max-w-lg w-full">
                <EnterNameStep onContinue={handleSignatureAdopt} />
              </div>
            )}
          </div>
        )}

        <GuestSignHeader handleSubmitSignatures={() => { }} />
        <div className={`sign_body ${step === 'disclosure' || step === 'adoptingSignature' || isProcessing ? 'pointer-events-none' : ''}`}>
          <div className="sign_left">
            <div className="sign_right_body" ref={pdfContainerRef}>
              <GuestPDFViewerControls
                numPages={numPages}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
              <div
                ref={scrollContainerRef}
                className="pdfViewer"
                style={{ maxHeight: "calc(100vh - 250px)" }}
              >
                <PDFViewer
                  fileUrl={fileBlobUrl}
                  containerWidth={containerWidth || 800}
                  signatureFields={signatureFields}
                  onBoxSelect={(boxId) => {
                    const box = requestData.signatureBoxes.find(b => b.id === boxId);
                    if (box) handleSignBox(box);
                  }}
                  isSigning={true}
                  currentPage={currentPage}
                  setNumPages={setNumPages}
                  selectedBoxId={selectedBoxId}
                  selectedSignerEmail={requestData.signerEmail}
                  initialFieldIds={new Set<string>}
                />
              </div>
            </div>
          </div>
          <GuestSidebar
            activeNav={activeNav}
            toggleNav={setActiveNav}
            requestData={requestData}
            handleSelectBox={handleSelectBox}
            selectedBoxId={selectedBoxId}
            onSign={onSign}
            onFinishAndSign={onFinishAndSign}
          />
        </div>
        {showTextModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-80">
              <h3 className="text-lg font-semibold mb-4">Enter Text</h3>
              <input
                type="text"
                value={textModalValue}
                onChange={(e) => setTextModalValue(e.target.value)}
                className="w-full p-2 border rounded mb-4"
                placeholder="Enter text for this field"
              />
              <div className="flex justify-end space-x-2">
                <Button variant="secondary" onClick={() => setShowTextModal(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleTextModalSubmit}>Submit</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </GuestLayout>
  );
};

export default GuestSignPage;